import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";

import { ApiError, checkSlug, signUp } from "../api/client";
import { CountryPicker, CurrencyPicker, TimezonePicker } from "../ui/pickers";
import { WorkingDays } from "../ui/working-days";
import { addressFor, settleSlug, slugify, slugifyTyped } from "./slug";
import styles from "./signup.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * Self-serve sign-up — a practice bringing itself into existence (2026-09-05).
 *
 * ── What it asks for, and what it refuses to ask for ────────────────────────
 *
 * Ten fields, three of them optional, and **not one is a clinical or
 * operational value**. No opening hours, no chair count, no turnover, no
 * prices. Those are entered afterwards on the screens that own them, by
 * somebody who can see what they are choosing — a sign-up form that asked for
 * turnover minutes would be inventing a default the moment anybody left it
 * alone, which is exactly what *Data is configured, not coded* forbids.
 *
 * ── The subdomain is the only hard idea on the page ─────────────────────────
 *
 * Nobody arriving here has met the concept, so the field does three things at
 * once: it **fills itself in from the practice name** until somebody edits it,
 * it **shows the whole address** rather than the bare slug, because
 * `cedar-dental.dentacore.app` is what they will actually type every morning,
 * and it **checks availability while they type** so a collision is found before
 * the submit rather than after it.
 *
 * ── The Google field is optional and says why ───────────────────────────────
 *
 * It is not a sign-in method and nothing happens to it here. It becomes
 * `clinics.google_workspace_email` — the account that will own each chair's
 * calendar once there are chairs (decision G1) — so asking now costs one line
 * and saves a settings visit the practice would otherwise make only after
 * wondering where their calendars went.
 */
export function SignupPage(): React.JSX.Element {
  const [clinicName, setClinicName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [googleAccountEmail, setGoogleAccountEmail] = useState("");
  /**
   * **The working day, asked and not assumed** (QA4-D5). Every location the
   * practice opens starts with these days and hours, so they are left empty
   * for the practice to fill rather than pre-set to somebody else's week.
   */
  const [opensAt, setOpensAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [workingDays, setWorkingDays] = useState<number[]>([]);
  const dayIsSet =
    opensAt !== "" && closesAt !== "" && opensAt < closesAt && workingDays.length > 0;

  /**
   * Where the practice is and what it bills in — **shown, not assumed.**
   *
   * The first draft detected the timezone and hid it, and picked USD without
   * asking. Both were wrong in the same way: a value this application chose,
   * for a clinic, silently. The timezone still *starts* from the browser and
   * the currency from the country, because a sensible starting point is not the
   * same as an invented one — but all three are on the form and all three are
   * the practice's to change.
   */
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("USD");

  const [availability, setAvailability] = useState<
    { state: "idle" | "checking" } | { state: "free" } | { state: "taken"; reason: string }
  >({ state: "idle" });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * The browser's own timezone, and the practice's until they say otherwise.
   *
   * **Detected rather than defaulted.** A hardcoded `Asia/Beirut` would be a
   * clinic value baked into the app; the browser's answer is a fact about the
   * person filling the form, and it stays editable on the Settings screen.
   */
  const [timezone, setTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  });

  // What the field shows, and what it means. Kept apart so a hyphen typed
  // mid-name survives on screen while the value checked and sent stays one the
  // API's pattern accepts — see `slugifyTyped`.
  const typedSlug = slugTouched ? slug : slugify(clinicName);
  const effectiveSlug = settleSlug(typedSlug);

  /**
   * Availability, debounced.
   *
   * 400ms after the last keystroke, aborted when another arrives — a request
   * per character would be both rude to the server and racy on the way back,
   * since responses do not necessarily arrive in the order they were sent.
   */
  useEffect(() => {
    if (effectiveSlug.length === 0) {
      setAvailability({ state: "idle" });
      return;
    }

    setAvailability({ state: "checking" });
    const controller = new AbortController();
    const timer = setTimeout(() => {
      checkSlug(effectiveSlug, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setAvailability(
            result.available
              ? { state: "free" }
              : { state: "taken", reason: result.reason ?? "That address cannot be used." },
          );
        })
        .catch(() => {
          // An unreachable server is not a taken name. Say nothing rather than
          // claiming something false — the submit still checks properly.
          if (!controller.signal.aborted) setAvailability({ state: "idle" });
        });
    }, 400);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [effectiveSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const result = await signUp({
        clinicName: clinicName.trim(),
        slug: effectiveSlug,
        timezone,
        currency,
        ...(country === "" ? {} : { defaultCountry: country }),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        googleAccountEmail:
          googleAccountEmail.trim().length > 0 ? googleAccountEmail.trim() : undefined,
        defaultShiftStartsAt: opensAt,
        defaultShiftEndsAt: closesAt,
        defaultWorkingDays: workingDays,
      });

      /**
       * **A full page load, not a router navigation.** The session belongs at
       * the practice's own origin — `localStorage` is per-origin — so the
       * browser has to actually go there, and the hand-off is what carries the
       * sign-in across. `replace` keeps the token out of the back button.
       */
      window.location.replace(`${result.origin}/welcome/${encodeURIComponent(result.handoff)}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.page}>
      <h1>Set up your practice</h1>
      <p className={styles.lede}>
        This creates your practice and your own admin account. Everything else — locations, chairs,
        your team and your price list — you enter afterwards, and you can change any of it at any
        time.
      </p>

      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.sectionFirst}>
          <p className={styles.sectionTitle}>Your practice</p>

          <label htmlFor="signup-clinic">
            Practice name
            <RequiredMark />
          </label>
          <input
            id="signup-clinic"
            name="organization"
            autoComplete="organization"
            required
            value={clinicName}
            onChange={(event) => setClinicName(event.target.value)}
            data-testid="signup-clinic-name"
          />

          <label htmlFor="signup-slug">
            Your address on DentaCore
            <RequiredMark />
          </label>
          <input
            id="signup-slug"
            required
            value={typedSlug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(slugifyTyped(event.target.value));
            }}
            data-testid="signup-slug"
          />
          <p className={styles.address} data-testid="signup-address">
            <span className={styles.addressSlug}>{addressFor(effectiveSlug)}</span>
          </p>
          <p
            className={
              availability.state === "free"
                ? styles.free
                : availability.state === "taken"
                  ? styles.taken
                  : styles.checking
            }
            data-testid="signup-availability"
          >
            {availability.state === "free" && "That address is free."}
            {availability.state === "taken" && availability.reason}
            {availability.state === "checking" && "Checking…"}
            {availability.state === "idle" && "This is where you and your team will sign in."}
          </p>

          {/*
            Where you are and what you bill in. Both start somewhere sensible —
            the browser's own timezone, and the currency the country implies —
            and both are yours to change here or in Settings later. The currency
            is the one worth getting right now: it is effectively fixed once a
            price exists, because it supplies the exponent every stored amount
            is read with.
          */}
          <div className={styles.row}>
            <CountryPicker
              id="signup-country"
              label="Country"
              value={country}
              onChange={setCountry}
              testId="signup-country"
            />
            <CurrencyPicker
              id="signup-currency"
              label="Currency"
              value={currency}
              onChange={setCurrency}
              testId="signup-currency"
              required
            />
          </div>

          <TimezonePicker
            id="signup-timezone"
            label="Timezone"
            value={timezone}
            onChange={setTimezone}
            testId="signup-timezone"
            required
            note="Taken from your browser. Each location keeps its own once you open one."
          />
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Your working day</p>

          <div className={styles.row}>
            <div>
              <label htmlFor="signup-opens">
                Opens
                <RequiredMark />
              </label>
              <input
                id="signup-opens"
                type="time"
                value={opensAt}
                onChange={(event) => setOpensAt(event.target.value)}
                data-testid="signup-opens"
              />
            </div>
            <div>
              <label htmlFor="signup-closes">
                Closes
                <RequiredMark />
              </label>
              <input
                id="signup-closes"
                type="time"
                value={closesAt}
                onChange={(event) => setClosesAt(event.target.value)}
                data-testid="signup-closes"
              />
            </div>
          </div>
          <WorkingDays
            value={workingDays}
            onChange={setWorkingDays}
            label="Days you work"
            required
            testId="signup-days"
          />
          <p className={styles.hint}>
            Every location you open starts with these as its opening hours. Each location can change
            its own afterwards.
          </p>
          {opensAt !== "" && closesAt !== "" && opensAt >= closesAt && (
            <p className={styles.taken} data-testid="signup-hours-order">
              The day has to open before it closes.
            </p>
          )}
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>You</p>

          <div className={styles.row}>
            <div>
              <label htmlFor="signup-first">
                First name
                <RequiredMark />
              </label>
              <input
                id="signup-first"
                autoComplete="given-name"
                required
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                data-testid="signup-first-name"
              />
            </div>
            <div>
              <label htmlFor="signup-last">
                Last name
                <RequiredMark />
              </label>
              <input
                id="signup-last"
                autoComplete="family-name"
                required
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                data-testid="signup-last-name"
              />
            </div>
          </div>

          <label htmlFor="signup-email">
            Email
            <RequiredMark />
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            data-testid="signup-email"
          />

          <label htmlFor="signup-password">
            Password
            <RequiredMark />
          </label>
          <input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            data-testid="signup-password"
          />
          <p className={styles.hint}>
            At least 12 characters. Length is what matters — there are no symbol rules.
          </p>
        </div>

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Google Calendar</p>

          <label htmlFor="signup-google">Your practice&rsquo;s Google account</label>
          <input
            id="signup-google"
            type="email"
            autoComplete="email"
            value={googleAccountEmail}
            onChange={(event) => setGoogleAccountEmail(event.target.value)}
            data-testid="signup-google"
          />
          <p className={styles.hint}>
            If you give us one, each chair you create gets its own Google calendar owned by this
            account, so your bookings show up in Google automatically. You can add or change it
            later in System, and leaving it blank changes nothing.
          </p>
        </div>

        {error !== null && (
          <p role="alert" className={styles.error} data-testid="signup-error">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button
            type="submit"
            disabled={submitting || availability.state === "taken" || !dayIsSet}
            data-testid="signup-submit"
          >
            {submitting ? "Setting it up…" : "Create my practice"}
          </button>
        </div>
      </form>

      <p className={styles.footnote}>
        Already have an account? <Link to="/login">Sign in</Link>. If your practice is already on
        DentaCore, ask whoever set it up to add you — they can send you a link.
      </p>
    </main>
  );
}
